import { z } from 'zod';
import { ageSchema, boardMetricKeySchema, currencySchema, kpiStatusSchema, pillarSchema, productCodeSchema, productMetricSchema, targetDirectionSchema, unitSchema, } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { periodSchema } from '../../common/schemas/period.schemas.js';
import { completionBasisSchema } from '../../reports/schemas/reports.schemas.js';
import { weekSchema } from '../../weeks/schemas/weeks.schemas.js';
/** How a metric's weekly values become one figure over a period (plan §8.1). */
export const boardAggregationSchema = z.enum(['SUM', 'AVERAGE', 'LATEST']);
/** The five journey stages; each has one sheet set (plan §8.3). */
export const boardStageSchema = z.enum(['GETTING', 'SELLING', 'SERVICING', 'PARTS', 'FINANCE']);
/** How a sheet is drawn; one `SheetCard` renders every kind. */
export const boardSheetKindSchema = z.enum(['FUNNEL', 'TILES', 'BARS_VS_TARGET', 'STACKED_COLUMNS', 'TABLE']);
/** The KPI a metric key is bound to, as the board shows it. */
export const boundKpiSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    pillar: pillarSchema,
    unit: unitSchema,
    currency: currencySchema.nullable(),
    status: kpiStatusSchema,
});
// ---------------------------------------------------------------------------------------------------------------
// Bindings
export const boardBindingSchema = z.object({
    key: boardMetricKeySchema,
    label: z.string(),
    /** The unit a bound KPI must have. */
    expectedUnit: unitSchema,
    /** `JOD` for every Currency key — the Age gates and the P&L lines are in JOD; `null` otherwise. */
    expectedCurrency: currencySchema.nullable(),
    aggregation: boardAggregationSchema,
    /** Titles of the sheets (and the Direction page) the key feeds. */
    feeds: z.array(z.string()),
    /** `null` = unbound: the slot renders `—` with a hint, never 0. */
    kpi: boundKpiSchema.nullable(),
    updatedAt: z.string().datetime().nullable(),
});
export const listBoardBindingsInputSchema = z.object({});
export const listBoardBindingsOutputSchema = z.object({
    items: z.array(boardBindingSchema),
});
export const updateBoardBindingInputSchema = z.object({
    key: boardMetricKeySchema,
    /** `null` unbinds the key. */
    kpiId: uuidSchema.nullable(),
});
// ---------------------------------------------------------------------------------------------------------------
// Direction & Ages
export const directionInputSchema = z.object({
    /** The year whose Age is "current"; defaults to the year of the current reporting week. */
    year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export const ageStatusSchema = z.enum(['PAST', 'CURRENT', 'FUTURE']);
export const ageProgressSchema = z.object({
    /** The weeks counted: the age year's weeks up to the as-of week. */
    fromWeek: weekSchema,
    toWeek: weekSchema,
    /** `SUM(VOLUME_DELIVERED)` over those weeks; `null` when unbound or without any value — never 0 for "no data". */
    units: z.number().nullable(),
    /** `SUM(VEHICLE_REVENUE)` over those weeks (JOD); `null` likewise. */
    revenue: z.number().nullable(),
    unitsPct: z.number().nullable(),
    revenuePct: z.number().nullable(),
    /** Units of the trailing four reporting weeks ending at `toWeek` — "last 4 weeks", never called a month. */
    runRate: z.object({
        units: z.number().nullable(),
        fromWeek: weekSchema,
        toWeek: weekSchema,
        weeks: z.number().int().min(1),
    }),
});
export const ageCardSchema = z.object({
    age: ageSchema,
    label: z.string(),
    year: z.number().int(),
    unitGate: z.number(),
    /** JOD. */
    revenueGate: z.number(),
    exitLine: z.string(),
    status: ageStatusSchema,
    /** `null` for a future age (gates only) or when the year has no reporting weeks yet. */
    progress: ageProgressSchema.nullable(),
});
export const directionOutputSchema = z.object({
    year: z.number().int(),
    /** The current reporting week (Sunday on/before today in Asia/Amman), if generated. */
    asOfWeek: weekSchema.nullable(),
    ages: z.array(ageCardSchema),
    /** The keys the Age cards read (`VOLUME_DELIVERED`, `VEHICLE_REVENUE`) that are unbound. */
    unbound: z.array(boardMetricKeySchema),
    /** What each read key is bound to. */
    bindings: z.array(z.object({ key: boardMetricKeySchema, kpi: boundKpiSchema.nullable() })),
});
// ---------------------------------------------------------------------------------------------------------------
// Stage sheets
export const stageSheetsInputSchema = z.object({
    stage: boardStageSchema,
    period: periodSchema,
});
export const sheetTargetSchema = z.object({
    name: z.string(),
    value: z.number(),
    direction: targetDirectionSchema,
});
/** One slot of a sheet: one metric key, aggregated over the period. */
export const sheetRowSchema = z.object({
    key: boardMetricKeySchema,
    label: z.string(),
    kpi: boundKpiSchema.nullable(),
    /** `null` = unbound, or bound without a value in the period — never 0. */
    value: z.number().nullable(),
    unit: unitSchema,
    currency: currencySchema.nullable(),
    aggregation: boardAggregationSchema,
    /** The applicable target at the To week (or, for "Deliveries vs Age gate", the current Age's unit gate). */
    target: sheetTargetSchema.nullable(),
    /** Which range figure the completion compares with the target (`completionBasis`, the rule Reports uses). */
    completionBasis: completionBasisSchema.nullable(),
    completionPct: z.number().nullable(),
    /** Funnels: this stage's value as a % of the previous stage's; `null` for the first stage or without both values. */
    previousStagePct: z.number().nullable(),
    /** Stacked columns: one value per week of the period; `null` is a gap. */
    series: z.array(z.object({ weekId: uuidSchema, weekDate: z.string(), value: z.number().nullable() })).nullable(),
});
export const sheetSchema = z.object({
    key: z.string(),
    title: z.string(),
    kind: boardSheetKindSchema,
    period: z.object({ label: z.string(), fromWeek: weekSchema, toWeek: weekSchema }),
    rows: z.array(sheetRowSchema),
    unbound: z.array(boardMetricKeySchema),
});
export const stageSheetsOutputSchema = z.object({
    stage: boardStageSchema,
    title: z.string(),
    sheets: z.array(sheetSchema),
});
// ---------------------------------------------------------------------------------------------------------------
// Products (Phase 8): per-model sold / delivered through `product_metric_bindings`
/** One figure of a product card: the SUM of the bound KPI over the period. */
export const productFigureSchema = z.object({
    /** `null` = unbound, or bound without a value in the period (or an inactive KPI) — never 0. */
    value: z.number().nullable(),
    /** `null` = unbound: the card shows `—` with the bind hint. */
    kpiId: uuidSchema.nullable(),
    aggregation: z.literal('SUM'),
});
export const productFiguresSchema = z.object({
    code: productCodeSchema,
    sold: productFigureSchema,
    delivered: productFigureSchema,
});
export const productSlotSchema = z.object({ code: productCodeSchema, metric: productMetricSchema });
export const productsInputSchema = z.object({
    period: periodSchema,
});
export const productsOutputSchema = z.object({
    period: z.object({ label: z.string(), fromWeek: weekSchema, toWeek: weekSchema }),
    items: z.array(productFiguresSchema),
    /** Every product × metric without a bound KPI, in page order. */
    unbound: z.array(productSlotSchema),
});
export const productBindingSchema = z.object({
    code: productCodeSchema,
    /** The model name, e.g. `Tiggo 4 HEV`. */
    model: z.string(),
    metric: productMetricSchema,
    /** `Vehicles sold`, `Vehicles delivered`. */
    label: z.string(),
    /** Always `NUMBER`: a product metric counts vehicles. */
    expectedUnit: unitSchema,
    aggregation: z.literal('SUM'),
    /** `null` = unbound. */
    kpi: boundKpiSchema.nullable(),
    updatedAt: z.string().datetime().nullable(),
});
export const listProductBindingsInputSchema = z.object({});
export const listProductBindingsOutputSchema = z.object({
    items: z.array(productBindingSchema),
});
export const updateProductBindingInputSchema = z.object({
    code: productCodeSchema,
    metric: productMetricSchema,
    /** `null` unbinds the metric. */
    kpiId: uuidSchema.nullable(),
});
//# sourceMappingURL=board.schemas.js.map